import { useId, useMemo, type CSSProperties } from 'react'
import type { LocalDate } from '../../platform/localDate'
import { Panel } from '../../platform/primitives/Panel'
import { HatchPattern } from '../../platform/charts/HatchPattern'
import { DivergingBars, type BarPick } from './charts/DivergingBars'
import { unitStart, type Granularity } from './cashflowPeriod'
import type { Period } from './cashflowWire'

type Props = {
  periods: Period[]
  granularity: Granularity
  currency: string
  /** The selected period's start. */
  selected: LocalDate
  /** The container owns the selection and emits `bar_selected`; `via` says how the pick was made. */
  onSelect: (start: LocalDate, via: BarPick) => void
  /** The server's deficit start dates (`deficitStarts`). */
  deficits: ReadonlySet<LocalDate>
  /** `totals.deficit_bucket_count` and `complete_bucket_count` of the history call (M3-R2a). */
  deficitCount: number
  completeCount: number
  /** The earliest transaction; periods before its unit are not drawn (M3-R12). */
  earliest: LocalDate | null
}

const BY: Record<Granularity, string> = { month: 'month', quarter: 'quarter', year: 'year' }
const PLURAL: Record<Granularity, string> = { month: 'months', quarter: 'quarters', year: 'years' }

/**
 * The sentence under the chart (M3-R2a), from the server's two counts. Null
 * when no period is complete: there is nothing yet to count.
 */
export function deficitCaption(deficitCount: number, completeCount: number, g: Granularity): string | null {
  if (completeCount <= 0) return null
  const noun = BY[g]
  if (completeCount === 1) {
    return deficitCount === 0 ? `The last ${noun} didn't spend more than came in.` : `The last ${noun} spent more than came in.`
  }
  if (deficitCount === 0) return `No ${noun} in the last ${completeCount} spent more than came in.`
  return `${deficitCount} of the last ${completeCount} ${PLURAL[g]} spent more than came in.`
}

/**
 * "Income and expense by {month|quarter|year}" (M3-R2, R3, R21): heading,
 * subtitle, a three-key legend and the diverging chart, which the heading
 * names. The deficit key carries the hatch as well as the colour, as the bar
 * does (rule 11). Every swatch is a class in `cashflow.css` (rules 1, 2).
 *
 * Periods before the earliest transaction's unit are dropped (M3-R12):
 * filtering a list, never summing money. The caption reads the server's counts.
 */
export function IncomeExpensePanel({
  periods,
  granularity,
  currency,
  selected,
  onSelect,
  deficits,
  deficitCount,
  completeCount,
  earliest,
}: Props) {
  const headingId = useId()
  const keyHatchId = `cf-key-hatch-${useId()}`
  const drawn = useMemo(() => {
    if (earliest === null) return periods
    const first = unitStart(earliest, granularity)
    return periods.filter((p) => p.start >= first)
  }, [periods, earliest, granularity])
  const caption = deficitCaption(deficitCount, completeCount, granularity)
  return (
    <Panel className="cf-panel">
      <div className="cf-panel__bar">
        <div className="cf-panel__head">
          <h2 id={headingId} className="cf-panel__title">
            Income and expense by {BY[granularity]}
          </h2>
          <p className="cf-panel__sub">Income above the line, spending below it — the gap is what you kept</p>
        </div>
        <ul className="cf-legend" aria-label="Legend">
          <li className="cf-legend__item">
            <span className="cf-legend__swatch cf-legend__swatch--income" aria-hidden="true" />
            Income
          </li>
          <li className="cf-legend__item">
            <span className="cf-legend__swatch cf-legend__swatch--expense" aria-hidden="true" />
            Expense
          </li>
          <li className="cf-legend__item">
            {/* The bar's own hatch at 1:1 (12 user units in 12px), through the key's own class: the
                bar's data classes stay on bars, so a count of .cf-bar--deficit is a count of bars. */}
            <svg
              className="cf-legend__swatch cf-legend__swatch--deficit"
              viewBox="0 0 12 12"
              aria-hidden="true"
              focusable="false"
              style={{ '--cf-hatch': `url(#${keyHatchId})` } as CSSProperties}
            >
              <defs>
                <HatchPattern id={keyHatchId} groundClass="cf-hatch__ground" stripeClass="cf-hatch__stripe" />
              </defs>
              <rect className="cf-legend__hatch" width="12" height="12" />
            </svg>
            Deficit
          </li>
        </ul>
      </div>
      <DivergingBars
        periods={drawn}
        granularity={granularity}
        currency={currency}
        selected={selected}
        onSelect={onSelect}
        deficits={deficits}
        labelledBy={headingId}
      />
      {caption !== null && <p className="cf-chart-caption">{caption}</p>}
    </Panel>
  )
}
